import { ends } from "@/lib/family";

const pairKey = (a, b) => [a, b].sort().join("|");
const relFact = (p) => p.sections?.find((s) => s.title === "Species Information")?.facts.find((f) => f.key === "Related Species");

// Role describes the source species S relative to the linked species T
const toEdge = (role, S, T) => {
  switch (role) {
    case "subspecies": return { kind: "pc", up: T, down: S, type: "subspecies" };
    case "hybrid": return { kind: "pc", up: T, down: S, type: "hybrid" };
    case "ancestor": return { kind: "pc", up: S, down: T, type: "descent" };
    case "descendant": return { kind: "pc", up: T, down: S, type: "descent" };
    case "cousin": return { kind: "sib", a: S, b: T, type: "cousin" };
    default: return { kind: "other", a: S, b: T, type: "other" };
  }
};

export function buildLineage(pages) {
  const species = pages.filter((p) => p.category === "Species");
  const byId = new Map(species.map((p) => [p.id, p]));
  const edges = new Map();
  species.forEach((S) => (relFact(S)?.items || []).forEach((it) => {
    if (!byId.has(it.page_id) || it.page_id === S.id) return;
    const e = toEdge(it.role, S.id, it.page_id);
    const k = pairKey(S.id, it.page_id);
    if (!edges.has(k) || (edges.get(k).kind !== "pc" && e.kind === "pc")) edges.set(k, e);
  }));
  const list = [...edges.values()];
  const linked = new Set(list.flatMap(ends));
  const degree = {};
  list.flatMap(ends).forEach((id) => { degree[id] = (degree[id] || 0) + 1; });
  return {
    byId, edges: list, ids: [...linked], degree, child: new Set(list.filter((e) => e.kind === "pc").map((e) => e.down)),
    loners: species.filter((s) => !linked.has(s.id)).sort((a, b) => a.title.localeCompare(b.title)),
    empty: species.length === 0,
  };
}

export const lineageTitle = ({ degree, child }) => (comp, byId) => {
  const top = [...comp].sort((a, b) => (degree[b] || 0) - (degree[a] || 0) || child.has(a) - child.has(b))[0];
  return `The ${byId.get(top).title} lineage`;
};

// A species plus everything above and below it, and the other parents of hybrids it helped create
export function focusLineage(tree, id) {
  const pc = tree.edges.filter((e) => e.kind === "pc");
  const walk = (start, next) => {
    const seen = new Set(), q = [start];
    while (q.length) next(q.shift()).forEach((n) => { if (!seen.has(n) && n !== start) { seen.add(n); q.push(n); } });
    return seen;
  };
  const downs = walk(id, (c) => pc.filter((e) => e.up === c).map((e) => e.down));
  const keep = new Set([id, ...walk(id, (c) => pc.filter((e) => e.down === c).map((e) => e.up)), ...downs]);
  pc.filter((e) => e.type === "hybrid" && downs.has(e.down)).forEach((e) => keep.add(e.up));
  return { ...tree, ids: [...keep], edges: tree.edges.filter((e) => ends(e).every((x) => keep.has(x))) };
}
